import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';
import AccessDenied from '../Error/AccessDenied';
import { Box, CircularProgress } from '@mui/material';

const PermissionGuard = ({ 
  children, 
  module, 
  action = 'view',
  fallbackMessage = null,
  showInLayout = false 
}) => {
  const { hasPermission, permissions, initialized, loading } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  
  // Check permission when permissions are initialized
  useEffect(() => {
    if (initialized && !loading) {
      const canAccess = hasPermission(module, action);
      setHasAccess(canAccess);
      setIsLoading(false);
      console.log(`🔍 [PermissionGuard] ${module}.${action} - hasAccess: ${canAccess}, initialized: ${initialized}`);
    } else if (!initialized && !loading) {
      // If not initialized and not loading, wait a bit more
      const timer = setTimeout(() => {
        const canAccess = hasPermission(module, action);
        setHasAccess(canAccess);
        setIsLoading(false);
        console.log(`🔍 [PermissionGuard] ${module}.${action} - hasAccess: ${canAccess} (fallback timer)`);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [module, action, hasPermission, permissions, initialized, loading]);
  
  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  // After loading, check if user has access
  if (!hasAccess) {
    const requiredPermission = `${module}.${action}`;
    const message = fallbackMessage || `You don't have permission to ${action} ${module.toLowerCase()}.`;

    // Show access denied message directly
    return (
      <AccessDenied 
        title="Access Denied"
        message={message}
        requiredPermission={requiredPermission}
        showContactSupport={true}
        // Respect showInLayout for consistent display
        style={showInLayout ? { border: '1px solid red', padding: '1rem' } : {}}
      />
    );
  }
  
  return children;
};

export default PermissionGuard; 